import React, { useEffect, useState } from "react";
import { FaSpinner, FaClipboardList, FaClock, FaCheckCircle, FaLock, FaHourglassEnd, FaArrowRight } from "react-icons/fa";

const API = process.env.REACT_APP_API_BASE_URL;

interface Assignment {
  id: number;
  classTestId: number;
  classTestTitle: string;
  level: string;
  chapterRange: string;
  scheduledStartTime: string | null;
  scheduledEndTime: string | null;
  totalDurationMinutes: number;
  effectiveStatus: "SCHEDULED" | "ACTIVE" | "COMPLETED";
  instructions: string | null;
  attemptId: number | null;
  attemptStatus: string | null;
  resultsPublished: boolean | null;
}

interface Props {
  onStart: (assignmentId: number) => void;
  onViewResult: (assignmentId: number) => void;
}

const LEVEL_COLORS: Record<string, string> = {
  A1: "bg-green-100 text-green-800",
  A2: "bg-blue-100 text-blue-800",
  B1: "bg-purple-100 text-purple-800",
  B2: "bg-orange-100 text-orange-800",
};

const fmt = (dt: string) =>
  new Date(dt).toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "Asia/Dhaka",
  });

const StudentClassTests: React.FC<Props> = ({ onStart, onViewResult }) => {
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState<number | null>(null);

  useEffect(() => {
    fetch(`${API}/api/student/class-tests/my-tests`, { credentials: "include" })
      .then((r) => r.json())
      .then(setAssignments)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const handleStart = async (assignmentId: number) => {
    setStarting(assignmentId);
    try {
      const res = await fetch(
        `${API}/api/student/class-tests/assignments/${assignmentId}/start`,
        { method: "POST", credentials: "include" }
      );
      if (res.ok) {
        onStart(assignmentId);
      } else {
        const d = await res.json().catch(() => ({}));
        alert(d.message || "Could not start test.");
      }
    } finally {
      setStarting(null);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <FaSpinner className="animate-spin text-3xl text-[#009fe3]" />
      </div>
    );
  }

  if (assignments.length === 0) {
    return (
      <div className="text-center py-16 text-gray-400">
        <FaClipboardList className="mx-auto text-4xl mb-3 text-gray-300" />
        <p className="font-medium">No class tests assigned yet.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {assignments.map((a) => {
        const isActive = a.effectiveStatus === "ACTIVE";
        const isScheduled = a.effectiveStatus === "SCHEDULED";
        const isCompleted = a.effectiveStatus === "COMPLETED";
        const submitted = a.attemptStatus != null && ["SUBMITTED","AUTO_SUBMITTED","GRADED","PARTIALLY_GRADED"].includes(a.attemptStatus);
        const inProgress = a.attemptStatus === "IN_PROGRESS";
        const canReview = submitted && a.resultsPublished && a.attemptId;

        const accentColor = isActive
          ? "bg-green-500"
          : isScheduled
          ? "bg-amber-400"
          : "bg-gray-300";

        return (
          <div
            key={a.id}
            className="relative bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md hover:border-[#009fe3]/30 transition-all overflow-hidden pl-5 pr-5 py-5 flex flex-col sm:flex-row sm:items-center gap-4"
          >
            <span className={`absolute left-0 top-0 bottom-0 w-1.5 ${accentColor}`} />

            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap mb-1.5">
                <span
                  className={`px-2 py-0.5 rounded-full text-xs font-bold ${LEVEL_COLORS[a.level] || "bg-gray-100 text-gray-700"}`}
                >
                  {a.level}
                </span>
                <span className="text-xs text-gray-500">
                  {a.chapterRange?.replace(/_/g, " ")}
                </span>
                {isActive && (
                  <span className="flex items-center gap-1 text-[10px] font-bold text-green-700 uppercase tracking-wide">
                    <span className="relative flex h-1.5 w-1.5">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
                      <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-green-500" />
                    </span>
                    Live now
                  </span>
                )}
              </div>
              <div className="font-semibold text-gray-900 text-base">{a.classTestTitle}</div>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 mt-2.5 text-xs text-gray-500">
                <span className="flex items-center gap-1 font-medium text-[#007cb5] bg-[#009fe3]/10 px-2 py-1 rounded-md">
                  <FaClock size={10} />
                  {a.totalDurationMinutes} min
                </span>
                {a.scheduledStartTime && a.scheduledEndTime && (
                  <span className="flex items-center gap-1.5">
                    {fmt(a.scheduledStartTime)}
                    <FaArrowRight size={9} className="text-[#009fe3]/50" />
                    {fmt(a.scheduledEndTime)}
                    <span className="text-gray-400 font-medium">(BD)</span>
                  </span>
                )}
              </div>
              {a.instructions && (
                <p className="mt-2 text-xs text-gray-500 italic border-l-2 border-gray-100 pl-2">{a.instructions}</p>
              )}
            </div>

            <div className="shrink-0 flex items-center gap-3">
              {isScheduled && (
                <div className="flex items-center gap-1.5 text-yellow-700 bg-yellow-50 border border-yellow-200 px-3 py-1.5 rounded-lg text-sm">
                  <FaLock size={12} />
                  Not yet available
                </div>
              )}
              {isActive && !submitted && !inProgress && (
                <button
                  onClick={() => handleStart(a.id)}
                  disabled={starting === a.id}
                  className="flex items-center gap-2 px-5 py-2 bg-[#009fe3] text-white font-medium rounded-lg hover:bg-[#007cb5] disabled:opacity-50 transition-colors"
                >
                  {starting === a.id ? <FaSpinner className="animate-spin" size={14} /> : <FaClipboardList size={14} />}
                  Start Test
                </button>
              )}
              {isActive && inProgress && (
                <button
                  onClick={() => handleStart(a.id)}
                  disabled={starting === a.id}
                  className="flex items-center gap-2 px-5 py-2 bg-amber-500 text-white font-medium rounded-lg hover:bg-amber-600 disabled:opacity-50 transition-colors"
                >
                  {starting === a.id ? <FaSpinner className="animate-spin" size={14} /> : <FaClipboardList size={14} />}
                  Continue Test
                </button>
              )}
              {submitted && !canReview && (
                <div className="flex items-center gap-1.5 text-green-700 bg-green-50 border border-green-200 px-3 py-1.5 rounded-lg text-sm">
                  <FaCheckCircle size={12} />
                  Submitted — awaiting results
                </div>
              )}
              {canReview && (
                <button
                  onClick={() => onViewResult(a.attemptId!)}
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors"
                >
                  <FaCheckCircle size={13} />
                  View Results
                </button>
              )}
              {(isCompleted && !a.attemptId) && (
                <div className="flex items-center gap-1.5 text-gray-500 bg-gray-50 border border-gray-200 px-3 py-1.5 rounded-lg text-sm">
                  <FaHourglassEnd size={11} />
                  Test ended
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default StudentClassTests;
